// docs/83 PO8 — the inventory in a fight. While no foe is in the party's sight, a hero's gear may be
// changed as its Bonus action: each move on the paper doll is planned (the `gear_plan` row, sent
// whole each time) and Change Gear goes in the hero's Bonus slot. Nothing moves until the turn
// plays; until then the inventory shows the gear as planned, with the moved pieces marked.
import { useEffect, useMemo } from 'react';
import { useTable } from 'spacetimedb/react';
import { tables } from '../../bindings';
import { useEncounter } from '../../core/encounter/session';
import { useAuthoring } from '../../core/stores/authoring';
import { rpc } from '../../core/net/reducers';
import { CHANGE_GEAR } from '../../core/sim/actions';
import { planUnwear, planWear, upgradeSegs } from '../../core/sim/gear';
import type { GearItem, HeroGear } from './useHeroGear';

const NONE = -1n;

export interface GearMoveView { itemId: bigint; slot: number | null }

export interface FightGear {
  /** The hero is in the fight on screen (its pack is otherwise sealed). */
  inFight: boolean;
  /** Its gear may be planned now; else `why` says why not. */
  canPlan: boolean;
  why?: string;
  moves: GearMoveView[];
  /** The hero's gear as planned (the gear itself when nothing is). */
  view: HeroGear;
  /** Pieces whose place the plan changes. */
  planned: Set<bigint>;
  plan(itemId: bigint, slot: number | null): Promise<boolean>;
  clear(): Promise<void>;
}

/** The encounter's gear plans (every hero's; a few rows at most). */
function useGearPlans(encounterId: bigint | undefined) {
  const [rows] = useTable(tables.gearPlan.where((r) => r.encounterId.eq(encounterId ?? NONE)), { enabled: encounterId !== undefined });
  return rows;
}

/** A standing foe the party can see (the party's view holds only those): `fk_sim::engine::foes_in_sight`. */
export function foesInSight(units: ReadonlyArray<{ faction?: string; onBoard?: boolean; downed?: boolean; hp: number }> | undefined): boolean {
  return !!units?.some((u) => u.faction === 'enemy' && u.onBoard !== false && !u.downed && u.hp > 0);
}

/** `items` after `moves`, by the inventory's own rules (a move that no longer holds is skipped, as the module does). */
export function applyMoves(items: readonly GearItem[], moves: readonly GearMoveView[], opts: { cls: number; capacity: number }): GearItem[] {
  let cur = items.map((i) => ({ ...i }));
  for (const m of moves) {
    const it = cur.find((i) => i.id === m.itemId);
    if (!it) continue;
    if (m.slot === null) {
      if (!planUnwear(it, cur, opts.capacity).ok) continue;
      cur = cur.map((i) => (i.id === it.id ? { ...i, slot: null } : i));
      continue;
    }
    const p = planWear(it, m.slot, cur, opts);
    if (!p.ok || p.noop) continue;
    const gone = new Set(p.toPack.map((x) => x.id));
    cur = cur.map((i) => (i.id === it.id ? { ...i, slot: m.slot } : gone.has(i.id) ? { ...i, slot: null } : p.swapped?.item.id === i.id ? { ...i, slot: p.swapped.to } : i));
  }
  return cur;
}

export function useFightGear(g: HeroGear, heroId: bigint): FightGear {
  const encounterId = useEncounter((s) => s.encounterId);
  const controlled = useEncounter((s) => s.controlled);
  const foes = useEncounter((s) => foesInSight(s.snapshot?.units));
  const locked = useAuthoring((s) => [...s.rows.values()].some((r) => r.heroId === Number(heroId) && r.state === 'committed'));
  const rows = useGearPlans(encounterId);
  const h = g.hero;
  const inFight = !!h && h.status === 1 && encounterId !== undefined && h.activeEncounter === encounterId;
  const mine = controlled.includes(Number(heroId));
  const why = !inFight ? undefined : !mine ? 'another player plans for this hero' : foes ? 'a foe is in sight' : locked ? 'the plan is locked' : undefined;
  const row = inFight ? rows.find((r) => r.heroId === heroId) : undefined;
  const moves = useMemo<GearMoveView[]>(() => (row?.moves ?? []).map((m) => ({ itemId: m.itemId, slot: m.slot ?? null })), [row]);
  const cls = h?.class ?? 0;
  const view = useMemo<HeroGear>(() => {
    if (!moves.length) return g;
    const items = applyMoves(g.items, moves, { cls, capacity: g.capacity });
    const worn = new Map(items.filter((i) => i.slot !== null).map((i) => [i.slot!, i]));
    const pack = items.filter((i) => i.slot === null);
    const on = items.filter((i) => i.slot !== null);
    return { ...g, items, worn, pack, stats: g.previewWorn(on.flatMap((i) => [i.slot!, i.defId, i.tier]), upgradeSegs(on)) };
  }, [g, moves, cls]);
  const planned = useMemo(() => {
    const was = new Map(g.items.map((i) => [i.id, i.slot]));
    return new Set(view.items.filter((i) => was.get(i.id) !== i.slot).map((i) => i.id));
  }, [g.items, view.items]);
  const send = async (next: GearMoveView[]) => {
    if (encounterId === undefined) return false;
    const r = await rpc.setGearPlan(encounterId, heroId, next.map((m) => ({ itemId: m.itemId, slot: m.slot ?? undefined })));
    return r.ok;
  };
  return {
    inFight, canPlan: inFight && !why, ...(why ? { why } : {}), moves, view, planned,
    async plan(itemId, slot) {
      if (!(await send([...moves, { itemId, slot }]))) return false;
      useAuthoring.getState().changeGear(Number(heroId));
      return true;
    },
    async clear() {
      await send([]);
      const a = useAuthoring.getState();
      const held = a.local.get(`${heroId}:B`) ?? a.rows.get(`${heroId}:B`);
      if (held?.action === CHANGE_GEAR) a.clear(Number(heroId), 'B');
    },
  };
}

/**
 * Mounted by the encounter HUD: a plan whose Change Gear has left the Bonus slot (Undo, another
 * bonus picked) would never happen, so it goes too — the inventory never shows a plan that won't.
 */
export function useGearPlanUpkeep(): void {
  const encounterId = useEncounter((s) => s.encounterId);
  const controlled = useEncounter((s) => s.controlled);
  const rows = useGearPlans(encounterId);
  const local = useAuthoring((s) => s.local);
  const intents = useAuthoring((s) => s.rows);
  useEffect(() => {
    if (encounterId === undefined) return;
    const orphans = rows.filter((r) => controlled.includes(Number(r.heroId)) && r.moves.length > 0 && (local.get(`${r.heroId}:B`) ?? intents.get(`${r.heroId}:B`))?.action !== CHANGE_GEAR);
    if (!orphans.length) return;
    const t = setTimeout(() => { for (const r of orphans) void rpc.setGearPlan(encounterId, r.heroId, []); }, 600);
    return () => clearTimeout(t);
  }, [encounterId, controlled, rows, local, intents]);
}
